import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getQuarter } from '@/helpers/date';
import { fetchGenderReport } from '@/api/insights';
import { format } from 'date-fns';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { isEmpty } from 'lodash-es';
import { getPassthroughRate } from '@/stores/insights/helpers';
import { assignInsightsTableToCSVDownloadLink } from '@/helpers/fileDownload';
import { numberWithCommas } from '@/helpers/formatters';
import { useDatasetCounts } from './datasetCounts';

export const useGenderReport = defineStore('genderReport', () => {
  const data = ref({
    male: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    female: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    declined: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    unknown: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
  });
  const error = ref(null);

  const isEmptyData = computed(() => isEmpty(data.value)
    || Object.values(data.value)
      .every((item) => Object.values(item || {}).every((value) => value === 0)));

  const tableRows = computed(() => {
    const {
      male, female, unknown,
    } = data.value || {};

    const rows = [
      {
        label: 'Female',
        review: {
          value: female.review,
          percent: getPassthroughRate('review', 'screen', female),
        },
        screen: {
          value: female.screen,
          percent: getPassthroughRate('screen', 'assessment', female),
        },
        assessment: {
          value: female.assessment,
          percent: getPassthroughRate('assessment', 'offer', female),
        },
        offer: {
          value: female.offer,
          percent: getPassthroughRate('offer', 'hired', female),
        },
        hired: {
          value: female.hired,
        },
      },
      {
        label: 'Male',
        review: {
          value: male.review,
          percent: getPassthroughRate('review', 'screen', male),
        },
        screen: {
          value: male.screen,
          percent: getPassthroughRate('screen', 'assessment', male),
        },
        assessment: {
          value: male.assessment,
          percent: getPassthroughRate('assessment', 'offer', male),
        },
        offer: {
          value: male.offer,
          percent: getPassthroughRate('offer', 'hired', male),
        },
        hired: {
          value: male.hired,
        },
      },
      {
        label: 'Unknown',
        review: {
          value: unknown.review,
          percent: getPassthroughRate('review', 'screen', unknown),
        },
        screen: {
          value: unknown.screen,
          percent: getPassthroughRate('screen', 'assessment', unknown),
        },
        assessment: {
          value: unknown.assessment,
          percent: getPassthroughRate('assessment', 'offer', unknown),
        },
        offer: {
          value: unknown.offer,
          percent: getPassthroughRate('offer', 'hired', unknown),
        },
        hired: {
          value: unknown.hired,
        },
      },
    ];

    return rows.sort((a, b) => b.review.value - a.review.value);
  });

  const tableTotals = computed(() => {
    const {
      male, female, unknown, declined,
    } = data.value || {};

    const totalApplied = male.review + female.review + unknown.review + declined.review;
    const totalScreening = male.screen + female.screen + unknown.screen + declined.screen;
    const totalAssessment = male.assessment
    + female.assessment
    + unknown.assessment
    + declined.assessment;
    const totalOffer = male.offer + female.offer + unknown.offer + declined.offer;
    const totalHired = male.hired + female.hired + unknown.hired + declined.hired;

    const appliedToScreeningPassthroughRate = getPassthroughRate('applied', 'screening', {
      applied: totalApplied,
      screening: totalScreening,
    });
    const screeningToAssessmentPassthroughRate = getPassthroughRate('screening', 'assessment', {
      screening: totalScreening,
      assessment: totalAssessment,
    });
    const assessmentToOfferPassthroughRate = getPassthroughRate('assessment', 'offer', {
      assessment: totalAssessment,
      offer: totalOffer,
    });
    const offerToHiredPassthroughRate = getPassthroughRate('offer', 'hired', {
      offer: totalOffer,
      hired: totalHired,
    });

    return [
      {
        id: 'applied',
        value: totalApplied,
        percent: appliedToScreeningPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'screening',
        value: totalScreening,
        percent: screeningToAssessmentPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'assessment',
        value: totalAssessment,
        percent: assessmentToOfferPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'offer',
        value: totalOffer,
        percent: offerToHiredPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'hired',
        value: totalHired,
      },
    ];
  });

  const calculateCandidatesForSummary = () => {
    const {
      male,
      female,
      declined,
      unknown,
    } = data.value;

    return numberWithCommas((
      (male?.review || 0)
      + (female?.review || 0)
      + (declined?.review || 0)
      + (unknown?.review || 0)
    ) || 0);
  };

  const loadGenderReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const responseData = await fetchGenderReport({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      data.value = responseData;

      if (responseData.male
        && responseData.female
        && responseData.declined
        && responseData.unknown
      ) {
        const total = calculateCandidatesForSummary();
        useDatasetCounts().setDatasetCounts(total);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load gender report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const downloadGenderCSV = ({ dateRange, projectName }) => {
    assignInsightsTableToCSVDownloadLink({
      tableRows: tableRows.value,
      tableTotals: tableTotals.value,
      dateRange,
      projectName,
      page: 'gender-report',
    });
  };

  return {
    // State
    data,
    error,
    tableRows,
    tableTotals,

    // Computed
    isEmptyData,

    // Actions
    loadGenderReport,
    downloadGenderCSV,
  };
});
